import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import {
  CreateNewEndUserFormData,
  useCreateNewEndUserFormFields,
  FormField,
  CreateNewEndUserFields,
  CreateNewEndUserFormKeys,
} from '../../hooks/create-new-end-user-form';
import { useCreateNewEndUserFieldMapper } from '../../hooks/end-user-hook';
import Typography from '@mui/material/Typography';
import { DeatilsResponse } from '../../hooks/deal-details';
import { useTranslate } from '@im/react-localization';
import FormGroup from '@mui/material/FormGroup';
import { StyledText } from 'src/components/StyledTypography';
import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/system';
import { useSearchParams } from 'react-router-dom';

export const CreateNewEndUserForm = (props: {
  countryCode: string;
  details: DeatilsResponse | undefined;
  formData: Partial<Record<keyof CreateNewEndUserFields, string>>;
  formError: Partial<Record<keyof CreateNewEndUserFields, string>>;
  setFormData: (
    formData: Partial<Record<keyof CreateNewEndUserFields, string>>
  ) => void;
  setFormError: (
    formData: Partial<Record<keyof CreateNewEndUserFields, string>>
  ) => void;
  topFormError: string;
}) => {
  const [searchParams] = useSearchParams();
  const businessType = searchParams.get('businessType');
  const translate = useTranslate();
  const CreateNewEndUserFormFields = useCreateNewEndUserFormFields(
    businessType,
    props.details?.isaddressmatchrequired === 1,
    props.details?.businessType === 'Hybrid'
  );
  const createNewEndUserFieldMapper = useCreateNewEndUserFieldMapper(
    businessType,
    props.countryCode
  );
  const scrollRef = useRef<HTMLDivElement | null>();
  const [dataPopulateType] = useState<string>('From bid');
  const theme = useTheme();

  useEffect(() => {
    props.setFormData(
      createNewEndUserFieldMapper(dataPopulateType, props.details)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataPopulateType]);
  useLayoutEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [props.topFormError]);
  const handleFieldChange = (
    name: keyof CreateNewEndUserFormData,
    value: string,
    fieldType?: FormField['fieldType']
  ) => {
    let _value;
    if (fieldType === 'number') {
      _value = isNaN(Number(value)) ? props.formData[name] : value.trim();
    } else {
      _value = value;
    }
    props.setFormData({ ...props.formData, [name]: _value });
    props.setFormError({ ...props.formError, [name]: '' });
  };
  return (
    <>
      <FormGroup>
        <StyledText>{translate('VendorDeals.EndUserPopulateWith')}</StyledText>
        <FormControl>
          <RadioGroup value={dataPopulateType}>
            <FormControlLabel
              control={<Radio />}
              label={
                <StyledText>
                  {translate('VendorDeals.EndUserPopulateWithFromBid')}
                </StyledText>
              }
              value="From bid"
            />
          </RadioGroup>
        </FormControl>
      </FormGroup>
      <Stack
        marginRight={-2}
        overflow="auto"
        paddingRight={2}
        ref={scrollRef}
        spacing={1.5}
      >
        {props.topFormError && (
          <StyledText
            sx={(theme: any) => ({ color: theme.palette.error.main })}
          >
            {props.topFormError}
          </StyledText>
        )}

        {Object.keys(CreateNewEndUserFormFields).map((key) => {
          const formValue =
            props.formData[key as keyof CreateNewEndUserFormData];
          const field: FormField = CreateNewEndUserFormFields[
            key as keyof CreateNewEndUserFormData
          ] as FormField;
          const fieldError = props.formError?.[key as CreateNewEndUserFormKeys];
          return (
            <>
              {((field.isHeader === true && businessType === 'Subscription') ||
                props.details?.businessType === 'Hybrid') && (
                <>
                  <Divider
                    sx={{
                      borderColor: theme.palette.neutral[200],
                      marginBottom: `8px`,
                      marginTop: `20px`,
                    }}
                  />
                  <Typography
                    fontWeight={'bold'}
                    lineHeight={`24px`}
                    variant="body2"
                  >
                    {field.headerValue}
                  </Typography>
                </>
              )}
              {(field.visibleToBusinessType === 'All' ||
                field.visibleToBusinessType === businessType ||
                props.details?.businessType === 'Hybrid') && (
                <InputFieldContainer
                  field={field}
                  fieldError={fieldError}
                  fieldKey={key as keyof CreateNewEndUserFormData}
                  fieldValue={formValue}
                  handleFieldChange={handleFieldChange}
                  key={key}
                />
              )}
            </>
          );
        })}
      </Stack>
    </>
  );
};

interface InputFieldContainerProps {
  field: FormField;
  fieldError?: string;
  fieldKey: keyof CreateNewEndUserFormData;
  fieldValue: CreateNewEndUserFormData[keyof CreateNewEndUserFormData];
  handleFieldChange: (
    name: keyof CreateNewEndUserFormData,
    value: string,
    fieldType?: FormField['fieldType']
  ) => void;
}
const InputFieldContainer = (props: InputFieldContainerProps) => {
  return (
    <Stack spacing={0.5}>
      <StyledText>
        {props.field.name}
        {props.field.required && (
          <Typography
            component="span"
            sx={(theme) => ({
              color: theme.palette.error.main,
              fontSize: theme.typography.body2.fontSize,
              lineHeight: theme.typography.body2.lineHeight,
            })}
          >{` *`}</Typography>
        )}
      </StyledText>
      <TextField
        disabled={props.field.disabled}
        error={Boolean(props.fieldError ?? '')}
        fullWidth
        helperText={props.fieldError ?? ''}
        id={props.fieldKey}
        inputProps={{ 'data-testid': `input-${props.fieldKey}` }}
        maxRows={4}
        multiline={props.field.multiline}
        name={props.fieldKey}
        onChange={(event) =>
          props.handleFieldChange(
            props.fieldKey,
            event.target.value,
            props.field.fieldType
          )
        }
        placeholder={props.field.placeholder}
        select={Boolean(props.field.options?.length)}
        size="small"
        value={props?.fieldValue ?? ''}
      >
        {props.field.options?.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
};
